import React from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import Icon from '@node-space/storybook-components/dist/Icon'
import Modal, { ModalActions, ModalBody } from '@node-space/storybook-components/dist/Modal'
import { StatusCard } from '@node-space/storybook-components/dist/StatusCard'
import { logSentryErrorAndGetTraceId } from '@node-space/utils'
import { RequestError } from 'components/Errors/RequestError'
import { useDeleteContactMutation } from 'hooks/mutations/useDeleteContactMutation'
import { useMappedErrorMessage } from 'hooks/useMappedErrorMessage'
import { useToastContext } from 'hooks/useToastContext'
import { reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BaseErrorResponse } from 'types/beneficiaries'
import { ContactInstrumentProps } from 'types/contacts'
import { contactAtom } from '../atoms/contactAtoms'

export interface ContactDeleteModalProps {
  isModalOpen: boolean
  onModalClose: () => void
}

export const ContactDeleteModal = ({ isModalOpen, onModalClose }: ContactDeleteModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const addToast = useToastContext()
  const contact = useAtomValue(contactAtom)
  const { requestError, setRequestError, resetRequestError } = useMappedErrorMessage()
  const { mutate: deleteContact, isPending: isDeletingContact } = useDeleteContactMutation()

  const handleDeleteContact = () => {
    const payload: ContactInstrumentProps = {
      contactId: contact?.id,
    }
    resetRequestError()
    deleteContact(payload, {
      onSuccess: () => {
        addToast({
          title: t('contacts.deleteContactSuccess', {
            contactName: contact?.name,
          }),
          state: 'success',
        })
        queryClient.invalidateQueries({ queryKey: [reactQueryName.CONTACTS] })
        onModalClose?.()
      },
      onError: (error: BaseErrorResponse) => {
        const sentryTraceId = logSentryErrorAndGetTraceId('deleteContact', error)
        setRequestError({
          handledMessage: error?.message,
          errorCode: error?.status,
          sentryTraceId,
          show: true,
        })
      },
    })
  }

  return (
    <Modal
      testid="contact-delete-modal"
      allowOverFlow
      visible={isModalOpen}
      closeModal={onModalClose}
    >
      <ModalBody>
        <StatusCard
          className="p-2"
          testid="contact-delete-modal-card"
          borderless
          customIcon={<Icon name="Prohibit" size={'hero'} color="status-error-500" />}
          headline={t('contacts.deleteContact')}
          description={t('contacts.deleteContactDescription')}
        />

        {requestError?.show && <RequestError>{requestError?.handledMessage}</RequestError>}
      </ModalBody>

      <ModalActions
        actions={[
          {
            destructive: true,
            children: t('contacts.delete'),
            testid: 'delete-contact-instrument',
            loading: isDeletingContact,
            iconElement: <Icon name="Prohibit" color="white" />,
            onClick: handleDeleteContact,
          },
          {
            secondary: true,
            children: t('cancel'),
            testid: 'cancel',
            onClick: onModalClose,
          },
        ]}
      />
    </Modal>
  )
}
